import { useState, useEffect } from "react";
import type { MemberRow } from "../../../../api/types";
import { createSubAdmin, fetchSubAdmins, patchSubAdmin, type SubAdminDto } from "../../../../api/subAdmins";
import { useAdminData } from "../../AdminDataContext";
import { getUserFacingMessage, reportError } from "../../../../api/errors";

interface AdminUser {
  id: string;
  name: string;
  email: string;
  role: "super" | "admin" | "editor" | "viewer";
  status: "active" | "disabled";
  createdAt: string;
  lastLogin: string;
  permissions: string[];
}

const roleConfig = {
  super: { label: "超级管理员", color: "bg-red-100 text-red-700", desc: "拥有所有权限" },
  admin: { label: "管理员", color: "bg-amber-100 text-amber-700", desc: "可管理会员、内容、活动" },
  editor: { label: "编辑员", color: "bg-green-100 text-green-700", desc: "仅可发布和编辑内容" },
  viewer: { label: "观察员", color: "bg-gray-100 text-gray-600", desc: "仅可查看数据" },
};

const allPermissions = [
  { id: "member.view", label: "查看会员", group: "会员管理" },
  { id: "member.audit", label: "审核会员", group: "会员管理" },
  { id: "member.edit", label: "编辑会员", group: "会员管理" },
  { id: "member.delete", label: "删除会员", group: "会员管理" },
  { id: "news.view", label: "查看资讯", group: "内容管理" },
  { id: "news.publish", label: "发布资讯", group: "内容管理" },
  { id: "news.delete", label: "删除资讯", group: "内容管理" },
  { id: "activity.view", label: "查看活动", group: "活动管理" },
  { id: "activity.manage", label: "管理活动", group: "活动管理" },
  { id: "activity.audit", label: "审核报名", group: "活动管理" },
  { id: "vote.view", label: "查看投票", group: "投票管理" },
  { id: "vote.manage", label: "管理投票", group: "投票管理" },
  { id: "settings.view", label: "查看设置", group: "系统设置" },
  { id: "settings.edit", label: "编辑设置", group: "系统设置" },
];

const genPassword = () => {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789@#!";
  return Array.from({ length: 10 }, () => chars[Math.floor(Math.random() * chars.length)]).join("");
};

function mapDtoToAdminUser(d: SubAdminDto): AdminUser {
  return {
    id: d.id,
    name: d.name,
    email: d.account,
    role: d.role,
    status: d.disabled ? "disabled" : "active",
    createdAt: "—",
    lastLogin: "—",
    permissions: d.permissions ?? [],
  };
}

export default function UserPermissions() {
  const { members, tenantLegacyId } = useAdminData();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [listErr, setListErr] = useState<string | null>(null);
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newUser, setNewUser] = useState({ name: "", account: "", password: "", role: "editor" as AdminUser["role"] });
  const [memberSearch, setMemberSearch] = useState("");
  const [showMemberDropdown, setShowMemberDropdown] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Reset password state
  const [showResetPwd, setShowResetPwd] = useState(false);
  const [resetPwdValue, setResetPwdValue] = useState("");
  const [showResetPwdValue, setShowResetPwdValue] = useState(false);
  const [resetDone, setResetDone] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setListErr(null);
    void fetchSubAdmins(tenantLegacyId)
      .then((rows) => {
        if (cancelled) return;
        setUsers(rows.map(mapDtoToAdminUser));
      })
      .catch((e) => {
        if (!cancelled) setListErr(getUserFacingMessage(e, "子账号列表加载失败，请刷新。"));
      });
    return () => {
      cancelled = true;
    };
  }, [tenantLegacyId]);

  const permGroups = Array.from(new Set(allPermissions.map((p) => p.group)));

  const filteredMembers = members.filter((m) =>
    !memberSearch || m.realName.includes(memberSearch) || m.name.includes(memberSearch)
  );

  const handleSelectMember = (m: MemberRow) => {
    setNewUser((p) => ({ ...p, name: m.realName, account: m.phone }));
    setMemberSearch(m.realName);
    setShowMemberDropdown(false);
  };

  const handleTogglePermission = (permId: string) => {
    if (!selectedUser || selectedUser.role === "super") return;
    const hasIt = selectedUser.permissions.includes(permId);
    const next = hasIt
      ? selectedUser.permissions.filter((p) => p !== permId)
      : [...selectedUser.permissions, permId];
    const updated = { ...selectedUser, permissions: next };
    setSelectedUser(updated);
    setUsers((prev) => prev.map((u) => (u.id === updated.id ? updated : u)));
    void patchSubAdmin(tenantLegacyId, selectedUser.id, { permissions: next }).catch((e) =>
      reportError(e),
    );
  };

  const handleToggleStatus = (id: string) => {
    const u = users.find((x) => x.id === id);
    if (!u || u.role === "super") return;
    const disabled = u.status === "active";
    void patchSubAdmin(tenantLegacyId, id, { disabled })
      .then((row) => {
        const mapped = mapDtoToAdminUser(row);
        setUsers((prev) => prev.map((x) => (x.id === id ? mapped : x)));
        setSelectedUser((p) => (p?.id === id ? mapped : p));
      })
      .catch((e) => reportError(e));
  };

  const handleAddUser = () => {
    if (!newUser.name || !newUser.account) return;
    const pwd = newUser.password || genPassword();
    const perms =
      newUser.role === "viewer" ? ["member.view", "news.view", "activity.view"] : [];
    void createSubAdmin(tenantLegacyId, {
      name: newUser.name.trim(),
      account: newUser.account.trim(),
      password: pwd,
      role: newUser.role,
      permissions: perms,
    })
      .then((d) => {
        setUsers((p) => [...p, mapDtoToAdminUser(d)]);
        setShowAddModal(false);
        setNewUser({ name: "", account: "", password: "", role: "editor" });
        setMemberSearch("");
      })
      .catch((e) => reportError(e));
  };

  const handleResetPassword = () => {
    if (!selectedUser || !resetPwdValue) return;
    void patchSubAdmin(tenantLegacyId, selectedUser.id, { password: resetPwdValue.trim() })
      .then(() => {
        setResetDone(true);
        setTimeout(() => {
          setResetDone(false);
          setShowResetPwd(false);
          setResetPwdValue("");
        }, 1500);
      })
      .catch((e) => reportError(e));
  };

  return (
    <div className="flex gap-6">
      {/* User List */}
      <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-semibold text-gray-800">用户列表</h3>
            {listErr && <p className="text-xs text-amber-600 mt-1">加载失败：{listErr}</p>}
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-1.5 px-4 py-2 bg-red-600 text-white text-sm rounded-xl hover:bg-red-700 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-user-add-line"></i> 添加用户
          </button>
        </div>
        <div className="space-y-2">
          {users.map((user) => (
            <div
              key={user.id}
              onClick={() => setSelectedUser(user)}
              className={`bg-white rounded-2xl p-4 cursor-pointer transition-all border-2 ${
                selectedUser?.id === user.id ? "border-red-400" : "border-transparent hover:border-gray-200"
              } ${user.status === "disabled" ? "opacity-60" : ""}`}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center bg-red-100 rounded-full flex-shrink-0">
                  <i className="ri-user-3-line text-red-600"></i>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-gray-800">{user.name}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${roleConfig[user.role].color}`}>
                      {roleConfig[user.role].label}
                    </span>
                    {user.status === "disabled" && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-400">已禁用</span>
                    )}
                  </div>
                  <p className="text-xs text-gray-400 mt-0.5">{user.email}</p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-xs text-gray-400">最后登录</p>
                  <p className="text-xs text-gray-600 font-medium">{user.lastLogin}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Permission Panel */}
      {selectedUser && (
        <div className="w-80 flex-shrink-0">
          <div className="bg-white rounded-2xl p-5 sticky top-0">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-800 text-sm">权限配置</h3>
              <button
                onClick={() => setSelectedUser(null)}
                className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer rounded-lg hover:bg-gray-100"
              >
                <i className="ri-close-line"></i>
              </button>
            </div>

            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl mb-4">
              <div className="w-9 h-9 flex items-center justify-center bg-red-100 rounded-full flex-shrink-0">
                <i className="ri-user-3-line text-red-600 text-sm"></i>
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-800">{selectedUser.name}</p>
                <p className="text-xs text-gray-400">{selectedUser.email}</p>
              </div>
            </div>

            <div className="mb-4">
              <p className="text-xs font-medium text-gray-600 mb-2">角色</p>
              <div className="grid grid-cols-2 gap-1.5">
                {(Object.keys(roleConfig) as AdminUser["role"][])
                  .filter((role) => role !== "super")
                  .map((role) => (
                  <button
                    key={role}
                    onClick={() => {
                      if (selectedUser.role === "super") return;
                      const updated = { ...selectedUser, role };
                      setSelectedUser(updated);
                      setUsers((p) => p.map((u) => u.id === updated.id ? updated : u));
                      void patchSubAdmin(tenantLegacyId, selectedUser.id, { role }).catch((e) =>
                        reportError(e),
                      );
                    }}
                    disabled={selectedUser.role === "super"}
                    className={`px-2 py-1.5 rounded-xl text-xs text-center cursor-pointer transition-all border-2 whitespace-nowrap ${
                      selectedUser.role === role
                        ? "border-red-500 " + roleConfig[role].color
                        : "border-transparent bg-gray-50 text-gray-500 hover:bg-gray-100"
                    } disabled:opacity-40 disabled:cursor-not-allowed`}
                  >
                    {roleConfig[role].label}
                  </button>
                ))}
              </div>
            </div>

            <div className="mb-4">
              <p className="text-xs font-medium text-gray-600 mb-2">细粒度权限</p>
              {selectedUser.role === "super" ? (
                <div className="text-xs text-gray-400 bg-gray-50 rounded-xl p-3 text-center">
                  超级管理员拥有所有权限
                </div>
              ) : (
                <div className="space-y-3 max-h-52 overflow-y-auto pr-1">
                  {permGroups.map((group) => (
                    <div key={group}>
                      <p className="text-xs font-medium text-gray-400 mb-1.5">{group}</p>
                      <div className="space-y-1">
                        {allPermissions.filter((p) => p.group === group).map((perm) => (
                          <label key={perm.id} className="flex items-center gap-2 cursor-pointer select-none group">
                            <input
                              type="checkbox"
                              checked={selectedUser.permissions.includes(perm.id)}
                              onChange={() => handleTogglePermission(perm.id)}
                              className="w-3.5 h-3.5 rounded accent-red-600"
                            />
                            <span className="text-xs text-gray-600 group-hover:text-gray-800 transition-colors">{perm.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Reset password button (non-super) */}
            {selectedUser.role !== "super" && (
              <button
                onClick={() => { setShowResetPwd(true); setResetPwdValue(""); setResetDone(false); }}
                className="w-full py-2 mb-2 text-sm bg-amber-50 text-amber-700 rounded-xl cursor-pointer hover:bg-amber-100 transition-all whitespace-nowrap"
              >
                <i className="ri-lock-password-line mr-1.5"></i>重置登录密码
              </button>
            )}

            {selectedUser.role !== "super" && (
              <button
                onClick={() => handleToggleStatus(selectedUser.id)}
                className={`w-full py-2 text-sm rounded-xl cursor-pointer transition-all whitespace-nowrap ${
                  selectedUser.status === "active"
                    ? "bg-gray-100 text-gray-600 hover:bg-red-50 hover:text-red-600"
                    : "bg-green-50 text-green-600 hover:bg-green-100"
                }`}
              >
                <i className={`${selectedUser.status === "active" ? "ri-forbid-line" : "ri-checkbox-circle-line"} mr-1.5`}></i>
                {selectedUser.status === "active" ? "禁用此账号" : "启用此账号"}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Add Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-[420px]">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-9 h-9 bg-red-50 rounded-xl flex items-center justify-center flex-shrink-0">
                <i className="ri-user-add-line text-red-600"></i>
              </div>
              <h3 className="font-bold text-gray-800">添加管理用户</h3>
            </div>
            <div className="space-y-4">
              {/* Member picker */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                  选择会员 <span className="text-red-500">*</span>
                  <span className="ml-1 text-gray-400 font-normal">（从会员列表中选择）</span>
                </label>
                <div className="relative">
                  <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                  <input
                    type="text"
                    value={memberSearch}
                    onChange={(e) => { setMemberSearch(e.target.value); setShowMemberDropdown(true); setNewUser((p) => ({ ...p, name: e.target.value, account: "" })); }}
                    onFocus={() => setShowMemberDropdown(true)}
                    placeholder="搜索会员姓名..."
                    className="w-full pl-9 pr-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:border-red-400"
                  />
                  {showMemberDropdown && filteredMembers.length > 0 && (
                    <div className="absolute z-10 top-full mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-lg max-h-52 overflow-y-auto">
                      {filteredMembers.slice(0, 8).map((m) => (
                        <button
                          key={m.id}
                          type="button"
                          onClick={() => handleSelectMember(m)}
                          className="w-full px-4 py-2.5 text-left hover:bg-gray-50 cursor-pointer transition-colors flex items-center gap-3"
                        >
                          <div className="w-7 h-7 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold text-red-600">
                            {m.realName.slice(0, 1)}
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-800">{m.realName}</p>
                            <p className="text-xs text-gray-400">{m.level} · {m.phone}</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Account (phone auto-filled) */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                  账户号 <span className="text-gray-400 font-normal">（默认为手机号）</span>
                </label>
                <input
                  type="text"
                  value={newUser.account}
                  onChange={(e) => setNewUser((p) => ({ ...p, account: e.target.value }))}
                  placeholder="选择会员后自动填入手机号"
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400"
                />
              </div>

              {/* Password */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">登录密码</label>
                <div className="flex gap-2">
                  <div className="flex-1 relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={newUser.password}
                      onChange={(e) => setNewUser((p) => ({ ...p, password: e.target.value }))}
                      placeholder="点击「随机生成」或手动输入"
                      className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400 pr-9 font-mono"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer"
                    >
                      <i className={showPassword ? "ri-eye-off-line text-sm" : "ri-eye-line text-sm"}></i>
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={() => setNewUser((p) => ({ ...p, password: genPassword() }))}
                    className="flex items-center gap-1.5 px-3 py-2.5 bg-amber-50 text-amber-700 text-xs rounded-xl hover:bg-amber-100 cursor-pointer transition-colors whitespace-nowrap border border-amber-200"
                  >
                    <i className="ri-refresh-line"></i> 随机生成
                  </button>
                </div>
                {newUser.password && (
                  <p className="text-xs text-amber-600 mt-1.5 flex items-center gap-1">
                    <i className="ri-information-line"></i>
                    请记录此密码，添加后无法再次查看
                  </p>
                )}
              </div>

              {/* Role */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">角色</label>
                <select
                  value={newUser.role}
                  onChange={(e) => setNewUser((p) => ({ ...p, role: e.target.value as AdminUser["role"] }))}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400 bg-white cursor-pointer"
                >
                  {(["admin", "editor", "viewer"] as const).map((role) => (
                    <option key={role} value={role}>{roleConfig[role].label} - {roleConfig[role].desc}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-5">
              <button onClick={() => { setShowAddModal(false); setMemberSearch(""); setNewUser({ name: "", account: "", password: "", role: "editor" }); }} className="px-4 py-2 text-sm text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 cursor-pointer whitespace-nowrap">取消</button>
              <button
                onClick={handleAddUser}
                disabled={!newUser.name || !newUser.account}
                className="px-4 py-2 text-sm text-white bg-red-600 rounded-xl hover:bg-red-700 cursor-pointer whitespace-nowrap disabled:opacity-50"
              >
                确认添加
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {showResetPwd && selectedUser && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-[400px]">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-9 h-9 bg-amber-50 rounded-xl flex items-center justify-center flex-shrink-0">
                <i className="ri-lock-password-line text-amber-600"></i>
              </div>
              <div>
                <h3 className="font-bold text-gray-800">重置登录密码</h3>
                <p className="text-xs text-gray-400 mt-0.5">{selectedUser.name} · {selectedUser.email}</p>
              </div>
              <button onClick={() => setShowResetPwd(false)} className="ml-auto w-7 h-7 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer rounded-lg hover:bg-gray-100">
                <i className="ri-close-line"></i>
              </button>
            </div>

            {resetDone ? (
              <div className="text-center py-4">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <i className="ri-check-line text-green-600 text-2xl"></i>
                </div>
                <p className="font-semibold text-green-700">密码重置成功</p>
                <p className="text-xs text-gray-400 mt-1">新密码已保存，请及时告知用户</p>
              </div>
            ) : (
              <>
                <div className="space-y-3 mb-5">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">新密码</label>
                    <div className="flex gap-2">
                      <div className="flex-1 relative">
                        <input
                          type={showResetPwdValue ? "text" : "password"}
                          value={resetPwdValue}
                          onChange={(e) => setResetPwdValue(e.target.value)}
                          placeholder="输入新密码或随机生成"
                          className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400 pr-9 font-mono"
                        />
                        <button
                          type="button"
                          onClick={() => setShowResetPwdValue((v) => !v)}
                          className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer"
                        >
                          <i className={showResetPwdValue ? "ri-eye-off-line text-sm" : "ri-eye-line text-sm"}></i>
                        </button>
                      </div>
                      <button
                        type="button"
                        onClick={() => setResetPwdValue(genPassword())}
                        className="flex items-center gap-1.5 px-3 py-2.5 bg-amber-50 text-amber-700 text-xs rounded-xl hover:bg-amber-100 cursor-pointer transition-colors whitespace-nowrap border border-amber-200"
                      >
                        <i className="ri-refresh-line"></i> 随机
                      </button>
                    </div>
                    {resetPwdValue && (
                      <p className="text-xs text-amber-600 mt-1.5 flex items-center gap-1">
                        <i className="ri-information-line"></i>
                        新密码：<span className="font-mono font-bold">{showResetPwdValue ? resetPwdValue : "•".repeat(resetPwdValue.length)}</span>
                        请记录，保存后无法再次查看
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex gap-3">
                  <button onClick={() => setShowResetPwd(false)} className="flex-1 py-2.5 text-sm text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 cursor-pointer whitespace-nowrap">取消</button>
                  <button
                    onClick={handleResetPassword}
                    disabled={!resetPwdValue}
                    className="flex-1 py-2.5 text-sm text-white bg-red-600 rounded-xl hover:bg-red-700 cursor-pointer whitespace-nowrap disabled:opacity-50"
                  >
                    确认重置
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
